import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { validateGSTIN, validateIFSC, validateMICR, validateAccountNo, validateAll } from "@/lib/validators";
import { telLink, whatsappLink } from "@/lib/mobileLinks";

const empty = {
  name: "", group: "Current Assets", opening_balance: 0, balance_type: "Dr",
  opening_balance_date: "", gstin: "", address: "", contact: "",
  bank_name: "", account_no: "", ifsc_code: "", micr_code: "",
  bank_address: "", account_type: "",
};

// Today as ISO yyyy-mm-dd, in the user's local timezone — matches what an
// accountant would enter when "today" is intended.
const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Schedule III categorisation for the ledger group dropdown — improves UX with 42 options.
const GROUP_CATEGORIES = {
  "Equity": ["Capital Account", "Reserves & Surplus"],
  "Non-current Liabilities": ["Long-term Borrowings", "Deferred Tax Liabilities", "Other Long-term Liabilities", "Long-term Provisions"],
  "Current Liabilities": ["Short-term Borrowings", "Sundry Creditors", "Trade Payables", "Other Current Liabilities", "Short-term Provisions", "Current Liabilities", "Loans", "Duties & Taxes"],
  "Non-current Assets": ["Property, Plant & Equipment", "Intangible Assets", "Capital Work-in-Progress", "Non-current Investments", "Deferred Tax Assets", "Long-term Loans & Advances", "Other Non-current Assets", "Fixed Assets"],
  "Current Assets": ["Current Investments", "Inventories", "Sundry Debtors", "Trade Receivables", "Bank Accounts", "Cash-in-Hand", "Short-term Loans & Advances", "Other Current Assets", "Current Assets"],
  "Income": ["Sales Accounts", "Direct Income", "Indirect Income", "Other Income"],
  "Expenses": ["Purchase Accounts", "Direct Expenses", "Indirect Expenses", "Employee Benefits Expense", "Finance Costs", "Depreciation & Amortization", "Other Expenses"],
};

function categorisedGroups(allGroups) {
  const known = new Set(Object.values(GROUP_CATEGORIES).flat());
  const out = [];
  Object.entries(GROUP_CATEGORIES).forEach(([cat, gs]) => {
    const present = gs.filter((g) => allGroups.includes(g));
    if (present.length) out.push({ cat, groups: present });
  });
  const others = allGroups.filter((g) => !known.has(g));
  if (others.length) out.push({ cat: "Other", groups: others });
  return out;
}

function Inner() {
  const { activeId } = useCompany();
  const [groups, setGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [filter, setFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    const [g, l] = await Promise.all([
      api.get("/ledger-groups"),
      api.get(`/companies/${activeId}/ledgers`),
    ]);
    setGroups(g.data);
    setLedgers(l.data);
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return ledgers.filter((l) => {
      if (groupFilter && l.group !== groupFilter) return false;
      if (filter && !l.name.toLowerCase().includes(filter.toLowerCase())) return false;
      return true;
    });
  }, [ledgers, filter, groupFilter]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...empty, opening_balance_date: todayISO() });
    setOpen(true);
  };

  const startEdit = (l) => {
    setEditing(l);
    setForm({
      name: l.name, group: l.group, opening_balance: l.opening_balance,
      balance_type: l.balance_type,
      opening_balance_date: l.opening_balance_date || todayISO(),
      gstin: l.gstin || "", address: l.address || "", contact: l.contact || "",
      bank_name: l.bank_name || "", account_no: l.account_no || "",
      ifsc_code: l.ifsc_code || "", micr_code: l.micr_code || "",
      bank_address: l.bank_address || "", account_type: l.account_type || "",
    });
    setOpen(true);
  };

  const save = async () => {
    // Client-side validation — catches typos before round-trip.
    const checks = [
      ["GSTIN", validateGSTIN(form.gstin)],
    ];
    if (form.group === "Bank Accounts") {
      checks.push(
        ["IFSC", validateIFSC(form.ifsc_code)],
        ["MICR", validateMICR(form.micr_code)],
        ["A/c No", validateAccountNo(form.account_no)],
      );
    }
    const v = validateAll(checks);
    if (!v.ok) { toast.error(v.msg); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_balance: Number(form.opening_balance) || 0,
        opening_balance_date: (form.opening_balance_date || "").trim() || todayISO(),
      };
      if (editing) {
        await api.put(`/ledgers/${editing.id}`, payload);
        toast.success("Ledger updated");
      } else {
        await api.post(`/companies/${activeId}/ledgers`, payload);
        toast.success("Ledger created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete ledger "${l.name}"?`)) return;
    await api.delete(`/ledgers/${l.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <>
      <PageHeader
        title="Ledgers"
        subtitle="Chart of accounts — create, edit and organise ledgers"
        actions={
          <Button onClick={startCreate} className="bg-blue-700 hover:bg-blue-800 h-8" data-testid="btn-new-ledger">
            <Plus className="w-4 h-4 mr-1" /> New Ledger
          </Button>
        }
      />
      <div className="p-6 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <Input placeholder="Search ledgers…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" data-testid="input-search-ledger" />
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="h-8 rounded border border-gray-300 bg-white px-2 text-sm" data-testid="select-filter-group">
            <option value="">All groups</option>
            {categorisedGroups(groups).map(({ cat, groups: gs }) => (
              <optgroup key={cat} label={cat}>
                {gs.map((g) => <option key={g} value={g}>{g}</option>)}
              </optgroup>
            ))}
          </select>
          <span className="ml-auto text-xs text-gray-500 self-center font-mono">{filtered.length} ledgers</span>
        </div>

        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table">
            <thead>
              <tr>
                <th>Name</th><th>Group</th><th className="text-right">Opening</th><th>Type</th><th>GSTIN</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-sm text-gray-400">No ledgers</td></tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`row-ledger-${l.id}`}>
                  <td className="font-medium">{l.name}</td>
                  <td><span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{l.group}</span></td>
                  <td className="tx-num">
                    {formatINR(l.opening_balance)}
                    {l.opening_balance_date && Number(l.opening_balance) !== 0 && (
                      <div className="text-[10px] font-mono text-gray-400 leading-tight">as on {l.opening_balance_date}</div>
                    )}
                  </td>
                  <td className={l.balance_type === "Dr" ? "text-blue-700" : "text-emerald-700"}>{l.balance_type}</td>
                  <td className="font-mono text-xs">{l.gstin || "—"}</td>
                  <td className="text-right space-x-1 whitespace-nowrap">
                    {l.contact && (
                      <>
                        <a
                          href={telLink(l.contact)}
                          className="text-gray-500 hover:text-blue-700 inline-block"
                          title={`Call ${l.contact}`}
                          data-testid={`btn-call-${l.id}`}
                          aria-label="Call"
                        >
                          <Phone className="w-4 h-4 inline" />
                        </a>
                        <a
                          href={whatsappLink(l.contact, `Hi ${l.name}, `)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-emerald-700 inline-block"
                          title={`WhatsApp ${l.contact}`}
                          data-testid={`btn-whatsapp-${l.id}`}
                          aria-label="WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4 inline" />
                        </a>
                      </>
                    )}
                    <button onClick={() => startEdit(l)} className="text-gray-500 hover:text-gray-900" data-testid={`btn-edit-ledger-${l.id}`}><Pencil className="w-4 h-4 inline" /></button>
                    <button onClick={() => remove(l)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 border-b border-gray-100">
            <DialogTitle>{editing ? "Edit Ledger" : "Create Ledger"}</DialogTitle>
            <DialogDescription className="sr-only">
              {editing ? "Update the ledger's name, group, opening balance and balance date." : "Add a new ledger to the chart of accounts. Opening balance and its date will drive the financial reports."}
            </DialogDescription>
          </DialogHeader>
          {/* Scrollable body — keeps the title and Save/Cancel footer fixed
              while bank fields, address, etc. can grow as long as needed.
              `max-h-[90vh]` on the parent + `overflow-y-auto` here means the
              dialog never extends past the viewport. */}
          <div className="grid grid-cols-2 gap-3 px-6 py-4 overflow-y-auto flex-1" data-testid="ledger-dialog-body">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-ledger-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Group *</Label>
              <select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className="h-8 w-full rounded border border-gray-300 px-2 text-sm" data-testid="select-ledger-group">
                {categorisedGroups(groups).map(({ cat, groups: gs }) => (
                  <optgroup key={cat} label={cat}>
                    {gs.map((g) => <option key={g} value={g}>{g}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Opening Balance</Label>
              <Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="input-ledger-opening" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Balance Type</Label>
              <select value={form.balance_type} onChange={(e) => setForm({ ...form, balance_type: e.target.value })} className="h-8 w-full rounded border border-gray-300 px-2 text-sm">
                <option value="Dr">Dr (Debit)</option>
                <option value="Cr">Cr (Credit)</option>
              </select>
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">
                Opening Balance Date
                <span className="ml-2 text-[10px] text-gray-400 normal-case tracking-normal">Defaults to today if blank. Drives whether this opening flows into the P&amp;L and Balance Sheet for a given period.</span>
              </Label>
              <Input
                type="date"
                value={form.opening_balance_date || ""}
                onChange={(e) => setForm({ ...form, opening_balance_date: e.target.value })}
                data-testid="input-ledger-opening-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">GSTIN</Label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="27ABCDE1234F1Z5" data-testid="input-ledger-gstin" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="input-ledger-address" />
            </div>

            {/* Bank-specific fields — visible only when group is Bank Accounts.
                Keeps the form lean for the 80% of ledgers that aren't bank a/cs. */}
            {form.group === "Bank Accounts" && (
              <div className="col-span-2 border-t border-gray-200 pt-3 mt-1">
                <h4 className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-3">Bank Account Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">Bank Name</Label>
                    <Input
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      placeholder="HDFC Bank"
                      data-testid="input-bank-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">Account Number</Label>
                    <Input
                      value={form.account_no}
                      onChange={(e) => setForm({ ...form, account_no: e.target.value })}
                      placeholder="50100123456789"
                      data-testid="input-account-no"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">IFSC Code</Label>
                    <Input
                      value={form.ifsc_code}
                      onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })}
                      placeholder="HDFC0001234"
                      maxLength={11}
                      data-testid="input-ifsc"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">MICR Code</Label>
                    <Input
                      value={form.micr_code}
                      onChange={(e) => setForm({ ...form, micr_code: e.target.value })}
                      placeholder="400240028"
                      maxLength={9}
                      data-testid="input-micr"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">Account Type</Label>
                    <select
                      value={form.account_type}
                      onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                      className="h-8 w-full rounded border border-gray-300 px-2 text-sm"
                      data-testid="select-account-type"
                    >
                      <option value="">— choose —</option>
                      <option value="Savings">Savings</option>
                      <option value="Current">Current</option>
                      <option value="OD">Overdraft (OD)</option>
                      <option value="CC">Cash Credit (CC)</option>
                      <option value="FD">Fixed Deposit (FD)</option>
                      <option value="RD">Recurring Deposit (RD)</option>
                      <option value="NRO">NRO</option>
                      <option value="NRE">NRE</option>
                    </select>
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">Bank Address (branch)</Label>
                    <Input
                      value={form.bank_address}
                      onChange={(e) => setForm({ ...form, bank_address: e.target.value })}
                      placeholder="Branch name, city, pincode"
                      data-testid="input-bank-address"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-save-ledger">
              {saving ? "Saving…" : (editing ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Ledgers() {
  return <RequireCompany><Inner /></RequireCompany>;
}
